import { forwardRef } from "react";

import { Stack as MuiStack, StackProps as MuiStackProps } from "@mui/material";

import { PartialBy } from "@app/common/types";

export interface StackProps extends MuiStackProps {
  variant?: "center" | "left" | "right" | "apart";
}
type StackAlignment = PartialBy<MuiStackProps, "justifyContent" | "alignItems">;

const centerProps: StackAlignment = {
  justifyContent: "center",
  alignItems: "center",
};

const leftProps: StackAlignment = {
  justifyContent: "start",
  alignItems: "start",
};

const leftRowProps: StackAlignment = {
  justifyContent: "start",
  alignItems: "center",
};

const rightProps: StackAlignment = {
  justifyContent: "end",
  alignItems: "end",
};

const rightRowProps: StackAlignment = {
  justifyContent: "end",
  alignItems: "center",
};

const apartProps: StackAlignment = {
  justifyContent: "space-between",
  alignItems: "center",
};

const PROPS_BY_VARIANT: { [key: string]: StackAlignment } = {
  center: centerProps,
  left: leftProps,
  right: rightProps,
  apart: apartProps,
  leftRow: leftRowProps,
  rightRow: rightRowProps,
  apartRow: apartProps,
  centerRow: centerProps,
};

const Row = forwardRef<HTMLDivElement | null, StackProps>(
  (props: StackProps, ref) => <Stack direction="row" {...props} ref={ref} />
);
Row.displayName = "Row";

const Column = forwardRef<HTMLDivElement | null, StackProps>(
  (props: StackProps, ref) => <Stack direction="column" {...props} ref={ref} />
);
Column.displayName = "Column";

/**
 *  Base Mui Stack component with added variants: `center`, `left`, `right`, `apart`
 */
const Stack = forwardRef<HTMLDivElement | null, StackProps>(
  ({ variant, ...props }: StackProps, ref) => {
    const pk =
      props.direction && props.direction === "row" ? `${variant}Row` : variant;
    return (
      <MuiStack
        useFlexGap
        {...(pk && PROPS_BY_VARIANT[pk])}
        {...props}
        ref={ref}
      />
    );
  }
);
Stack.displayName = "Stack";

export { Row, Column };
export default Stack;
